import axios from 'axios';

const backendUrl = process.env.REACT_APP_BACKEND_URL;
const apiKey = process.env.REACT_APP_OPEN_API_KEY;

export interface Estado {
  estadoId: number;
  sigla: string;
  nome: string;
}

export interface Municipio {
  municipioId: number;
  nome: string;
  estadoId: number;
  estadoSigla: string;
}

export interface LocalizacoesData {
  estados: Estado[];
  municipios: Municipio[];
}

let cachedLocalizacoes: LocalizacoesData | null = null;
let cacheKey = '';
const failedCacheKeys = new Set<string>();

function normalizeEstado(raw: Record<string, unknown>): Estado {
  return {
    estadoId: Number(raw.estadoId ?? raw.estado_id ?? 0),
    sigla: String(raw.sigla ?? raw.uf ?? '').toUpperCase(),
    nome: String(raw.nome ?? raw.name ?? ''),
  };
}

function normalizeMunicipio(raw: Record<string, unknown>, estadoFallback?: Estado): Municipio {
  return {
    municipioId: Number(raw.municipioId ?? raw.municipio_id ?? 0),
    nome: String(raw.nome ?? ''),
    estadoId: Number(raw.estadoId ?? raw.estado_id ?? estadoFallback?.estadoId ?? 0),
    estadoSigla: String(
      raw.estadoSigla ?? raw.estado_sigla ?? raw.sigla ?? estadoFallback?.sigla ?? ''
    ).toUpperCase(),
  };
}

function parseLocalizacoesResponse(dados: Record<string, unknown>): LocalizacoesData {
  const estadosRaw = Array.isArray(dados.estados) ? dados.estados : [];
  const estados = estadosRaw.map((item) => normalizeEstado(item as Record<string, unknown>));

  let municipios: Municipio[] = [];
  const municipiosRaw = Array.isArray(dados.municipios) ? dados.municipios : [];
  municipios = municipiosRaw.map((item) => normalizeMunicipio(item as Record<string, unknown>));

  if (municipios.length === 0) {
    for (const estadoItem of estadosRaw) {
      const estado = normalizeEstado(estadoItem as Record<string, unknown>);
      const nested = (estadoItem as Record<string, unknown>).municipios;
      if (!Array.isArray(nested)) continue;
      municipios.push(
        ...nested.map((item) =>
          normalizeMunicipio(item as Record<string, unknown>, estado)
        )
      );
    }
  }

  return { estados, municipios };
}

function getErrorMessage(error: any): string {
  const erro = error.response?.data?.erro;
  if (erro?.mensagem) return erro.mensagem;
  if (error.response?.data?.message) return error.response.data.message;
  return 'Erro ao carregar localizações';
}

export async function fetchEstadosEMunicipios(
  empresa?: string | null,
  filial?: string | null
): Promise<LocalizacoesData> {
  const key = `${empresa ?? ''}|${filial ?? ''}`;
  if (cachedLocalizacoes && cacheKey === key) {
    return cachedLocalizacoes;
  }
  if (failedCacheKeys.has(key)) {
    throw new Error('Estados e municípios indisponíveis.');
  }

  if (!apiKey) {
    throw new Error('REACT_APP_OPEN_API_KEY não configurada no .env');
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
  };
  if (empresa?.trim()) headers['x-empresa'] = empresa.trim();
  if (filial?.trim()) headers['x-filial'] = filial.trim();

  try {
    const response = await axios.get(`${backendUrl}/open-api/v1/localizacoes/estados-e-municipios`, {
      headers,
    });

    const dados = response.data?.dados;
    if (!response.data?.sucesso || !dados) {
      throw new Error(getErrorMessage({ response }));
    }

    const result = parseLocalizacoesResponse(dados as Record<string, unknown>);

    cachedLocalizacoes = result;
    cacheKey = key;
    return result;
  } catch (error: any) {
    if (error.response?.status === 404) {
      failedCacheKeys.add(key);
    }
    throw new Error(getErrorMessage(error));
  }
}

export function getMunicipiosByUf(
  municipios: Municipio[],
  uf: string,
  estados?: Estado[]
): Municipio[] {
  const sigla = uf.trim().toUpperCase();
  if (!sigla) return [];

  const bySigla = municipios.filter((m) => m.estadoSigla.toUpperCase() === sigla);
  if (bySigla.length > 0) return bySigla;

  const estado = estados?.find((e) => e.sigla.toUpperCase() === sigla);
  if (!estado?.estadoId) return [];

  return municipios.filter((m) => m.estadoId === estado.estadoId);
}

export function findMunicipioByName(municipios: Municipio[], cityName: string): Municipio | null {
  const normalized = cityName
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
  return (
    municipios.find(
      (m) =>
        m.nome
          .trim()
          .toLowerCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '') === normalized
    ) ?? null
  );
}
